import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Checkbox from '@material-ui/core/Checkbox';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import {
  fetchPreferences,
  updatePreferences,
  clearNotificationsState,
} from '../../../containers/actions/common/notificationsAction';

const ORANGE = '#e36e39';

// Categories are sorted alphabetically (A → Z) across all views.

// ─── styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 1.5),
    },
  },

  // ── top bar ────────────────────────────────────────────────────────────────
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  backBtn: {
    textTransform: 'none',
    fontSize: '0.875rem',
    color: '#333',
    padding: '2px 6px 2px 0',
    minWidth: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  saveBtn: {
    backgroundColor: ORANGE,
    color: '#fff',
    textTransform: 'none',
    fontSize: '0.875rem',
    padding: '4px 24px',
    borderRadius: '2rem',
    '&:hover': {
      backgroundColor: '#000',
      color: '#fff',
    },
  },

  // ── title ──────────────────────────────────────────────────────────────────
  titleSection: {
    marginBottom: theme.spacing(2.5),
  },
  title: {
    fontWeight: 500,
    fontSize: '1.5rem',
    textAlign: 'left',
    paddingBottom: '0.3rem',
  },
  titleBorder: {
    border: '2px solid #707070',
    display: 'flex',
    width: '1.6rem',
    backgroundColor: 'grey',
  },

  // ── tabs ───────────────────────────────────────────────────────────────────
  tabsRoot: {
    marginBottom: theme.spacing(1.5),
    '& .MuiTab-root:first-child': {
      paddingLeft: 0,
    },
  },
  tabsIndicator: {
    display: 'none',
  },
  tab: {
    textTransform: 'none',
    fontSize: '0.875rem',
    minWidth: 'auto',
    padding: '8px 16px',
    fontWeight: 400,
    '&.Mui-selected': {
      color: ORANGE,
      fontWeight: 'bold',
    },
    '&.Mui-selected .MuiTab-wrapper': {
      borderBottom: `2px solid ${ORANGE}`,
      paddingBottom: 2,
    },
  },

  // ── radio ──────────────────────────────────────────────────────────────────
  radioGroup: {
    flexDirection: 'row',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  radioRoot: {
    padding: 4,
    color: '#ccc',
    '&.Mui-checked': {
      color: ORANGE,
    },
  },
  radioLabel: {
    fontSize: '0.875rem',
    color: '#333',
  },

  // ── table ──────────────────────────────────────────────────────────────────
  tableWrapper: {
    overflowX: 'auto',
    width: '100%',
  },
  tablePaper: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  tableHead: {
    backgroundColor: '#fafafa',
    '& .MuiTableCell-head': {
      fontWeight: 'bold',
      fontSize: '0.875rem',
      color: '#333',
      padding: '8px 12px',
      borderBottom: '2px solid #e0e0e0',
      whiteSpace: 'nowrap',
    },
  },
  tableRow: {
    '&:hover': { backgroundColor: '#f9f9f9' },
  },
  tableCell: {
    padding: '8px 12px',
    fontSize: '0.875rem',
    color: '#333',
    borderBottom: '1px solid #f0f0f0',
  },
  checkboxRoot: {
    padding: 4,
    '&.Mui-checked': {
      color: ORANGE,
    },
  },
  disabledCheckboxRoot: {
    padding: 4,
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },

  // ── success dialog ────────────────────────────────────────────────────────
  successDialogPaper: {
    borderRadius: 12,
    padding: theme.spacing(3, 4, 4),
    maxWidth: 420,
    textAlign: 'center',
    position: 'relative',
  },
  successCloseRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1),
  },
  successIcon: {
    color: '#4caf50',
    fontSize: '3.5rem',
    marginBottom: theme.spacing(1.5),
  },
  successTitle: {
    fontWeight: 700,
    fontSize: '1.15rem',
    marginBottom: theme.spacing(1),
  },
  successBody: {
    fontSize: '0.9rem',
    color: '#555',
    lineHeight: 1.6,
    marginBottom: theme.spacing(2.5),
  },
  successBtn: {
    backgroundColor: ORANGE,
    color: '#fff',
    textTransform: 'none',
    fontSize: '0.875rem',
    padding: '6px 36px',
    borderRadius: '2rem',
    '&:hover': {
      backgroundColor: '#000',
      color: '#fff',
    },
  },

  // ── unsaved changes dialog ──────────────────────────────────────────────
  unsavedDialogPaper: {
    borderRadius: 12,
    padding: theme.spacing(3, 4, 4),
    maxWidth: 480,
    position: 'relative',
  },
  unsavedCancelRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 4,
    marginBottom: theme.spacing(2),
  },
  unsavedCancelText: {
    fontSize: '0.9rem',
    color: '#333',
    fontWeight: 500,
    cursor: 'pointer',
    lineHeight: 1,
  },
  unsavedTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  unsavedBody: {
    fontSize: '0.9rem',
    color: '#555',
    textAlign: 'center',
    marginBottom: theme.spacing(3),
    lineHeight: 1.6,
  },
  unsavedConfirmBtn: {
    display: 'block',
    margin: '0 auto',
    backgroundColor: ORANGE,
    color: '#fff',
    textTransform: 'none',
    fontSize: '0.9rem',
    padding: '6px 36px',
    borderRadius: '2rem',
    '&:hover': {
      backgroundColor: '#000',
      color: '#fff',
    },
  },
}));

// Acronyms that must always render in full caps in event names / labels.
const EVENT_NAME_ACRONYMS = new Set([
  'BMS', 'CMO', 'ABO', 'PO', 'EU', 'ASN', 'SAP', 'API',
  'GRN', 'PCMP', 'ERP', 'UOM', 'QC', 'QA', 'CC', 'SL', 'ID',
]);

const formatEventName = (name) => {
  if (!name) return '';
  return name
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')   // "CMOUser" -> "CMO User"
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')        // "batchAllocation" -> "batch Allocation"
    .replace(/[_-]+/g, ' ')                          // snake_case / kebab -> spaces
    .trim()
    .split(/\s+/)
    .map((w) => {
      const upper = w.toUpperCase();
      if (EVENT_NAME_ACRONYMS.has(upper)) return upper;            // BMS, CMO, PO, EU, ASN...
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(); // Title Case each word
    })
    .join(' ');
};

// ─── component ────────────────────────────────────────────────────────────────

const ManageNotifications = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const { preferenceEventTypes: rawEventTypes, preferences, preferencesLoading, updatePreferencesSuccess, updatePreferencesError } =
    useSelector((state) => state.pcmp.common.notifications);
  const preferenceEventTypes = Array.isArray(rawEventTypes) ? rawEventTypes : [];

  const [activeTab, setActiveTab] = useState(0);

  // Local draft of settings: { [eventTypeID]: { dashboard: bool, emailAlert: bool } }
  const [draft, setDraft] = useState({});

  // Original fetched state — used to restore when "Custom" radio is clicked
  const originalDraft = useRef({});

  // Success popup
  const [successOpen, setSuccessOpen] = useState(false);

  // Save in-progress
  const [saving, setSaving] = useState(false);

  // Dirty tracking — true when draft differs from the original fetched state
  const isDirty = React.useMemo(() => {
    const orig = originalDraft.current;
    if (!Object.keys(orig).length) return false;
    return Object.keys(draft).some((id) => {
      const d = draft[id];
      const o = orig[id];
      if (!o) return true;
      return d.dashboard !== o.dashboard || d.emailAlert !== o.emailAlert;
    });
  }, [draft]);

  // Unsaved changes confirmation dialog
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);

  // ── Derive sorted category labels from API data ─────────────────────────────
  const categoryLabels = React.useMemo(() => {
    const unique = [...new Set(preferenceEventTypes.map((et) => et.category))];
    return unique
      .filter((cat) => cat)
      .sort((a, b) => {
        if (a === 'Others') return 1;
        if (b === 'Others') return -1;
        return a.localeCompare(b);
      });
  }, [preferenceEventTypes]);

  // Per-category radio mode: 'all' | 'none' | 'custom'
  const [radioModes, setRadioModes] = useState({});

  // ── Load preferences on mount ───────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchPreferences());
  }, [dispatch]);

  // ── Initialise local draft + default radio to "custom" when API data arrives ─
  useEffect(() => {
    if (!preferenceEventTypes.length) return;
    const initial = {};
    preferenceEventTypes.forEach((et) => {
      const saved = preferences[et.eventTypeID];
      initial[et.eventTypeID] = {
        dashboard: saved ? saved.dashboard_notification : false,
        emailAlert: saved ? saved.email_notification : false,
      };
    });
    setDraft(initial);
    originalDraft.current = initial;

    // Default every category radio to "custom" (preferences fetched = custom state)
    const modes = {};
    categoryLabels.forEach((label) => {
      modes[label] = 'custom';
    });
    setRadioModes(modes);
  }, [preferenceEventTypes, preferences]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Show success popup when save succeeds, stop loader on success or error ─
  useEffect(() => {
    if (updatePreferencesSuccess) {
      setSaving(false);
      originalDraft.current = { ...draft };
      setSuccessOpen(true);
    }
  }, [updatePreferencesSuccess]);

  useEffect(() => {
    if (updatePreferencesError) {
      setSaving(false);
    }
  }, [updatePreferencesError]);

  // Reset tab if categories shrink below current index
  useEffect(() => {
    if (categoryLabels.length && activeTab >= categoryLabels.length) {
      setActiveTab(0);
    }
  }, [categoryLabels.length, activeTab]);

  // ── Events for the current tab ─────────────────────────────────────────────
  const currentLabel = categoryLabels[activeTab] || '';
  const currentEvents = preferenceEventTypes.filter(
    (et) => et.category === currentLabel
  );

  const handleRadioChange = (value) => {
    setRadioModes((prev) => ({ ...prev, [currentLabel]: value }));
    if (value === 'all' || value === 'none') {
      const checked = value === 'all';
      setDraft((prev) => {
        const next = { ...prev };
        currentEvents.forEach((et) => {
          next[et.eventTypeID] = { dashboard: checked, emailAlert: checked };
        });
        return next;
      });
    } else if (value === 'custom') {
      // Restore the original fetched preferences for this category
      setDraft((prev) => {
        const next = { ...prev };
        currentEvents.forEach((et) => {
          next[et.eventTypeID] = originalDraft.current[et.eventTypeID] || { dashboard: false, emailAlert: false };
        });
        return next;
      });
    }
  };

  const handleCheckboxChange = (eventTypeID, field) => {
    setDraft((prev) => ({
      ...prev,
      [eventTypeID]: {
        ...prev[eventTypeID],
        [field]: !prev[eventTypeID][field],
      },
    }));
    setRadioModes((prev) => ({ ...prev, [currentLabel]: 'custom' }));
  };

  const handleSave = () => {
    setSaving(true);
    // Build mappings array from the entire draft (all categories, not just current tab)
    // Send eventToUserMappingID (from preferences map) instead of eventTypeID
    const mappings = Object.entries(draft).map(([eventTypeID, s]) => ({
      ...(preferences[eventTypeID]?.eventToUserMappingID
        ? { eventToUserMappingID: preferences[eventTypeID].eventToUserMappingID }
        : { eventTypeID: Number(eventTypeID) }),
      dashboardNotification: s.dashboard,
      emailNotification: s.emailAlert,
    }));
    dispatch(updatePreferences(mappings));
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    dispatch(clearNotificationsState());
  };

  const loading = preferencesLoading;

  return (
    <div className={classes.root}>

      {/* ── top bar: back + save ─────────────────────────────────────────── */}
      <div className={classes.topBar}>
        <Button
          className={classes.backBtn}
          disableRipple
          startIcon={<KeyboardArrowLeftIcon style={{ fontSize: '1.5rem', marginRight: -2 }} />}
          onClick={() => isDirty ? setUnsavedDialogOpen(true) : history.goBack()}
        >
          Back
        </Button>
        <Button
          className={classes.saveBtn}
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={loading || saving || !isDirty}
        >
          Save
          {saving && <CircularProgress size={16} style={{ color: '#fff', marginLeft: 8 }} />}
        </Button>
      </div>

      {/* ── title ────────────────────────────────────────────────────────── */}
      <div className={classes.titleSection}>
        <Typography variant="h4" className={classes.title}>Manage Notifications</Typography>
        <span className={classes.titleBorder}></span>
      </div>

      {/* ── category tabs ────────────────────────────────────────────────── */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        className={classes.tabsRoot}
        classes={{ indicator: classes.tabsIndicator }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {categoryLabels.map((label) => {
          const count = preferenceEventTypes.filter((et) => et.category === label).length;
          return (
            <Tab
              key={label}
              label={`${formatEventName(label)} (${count})`}
              className={classes.tab}
            />
          );
        })}
      </Tabs>

      {/* ── radio group ──────────────────────────────────────────────────── */}
      <RadioGroup
        value={radioModes[currentLabel] || 'custom'}
        onChange={(e) => handleRadioChange(e.target.value)}
        className={classes.radioGroup}
      >
        <FormControlLabel
          value="all"
          control={<Radio size="small" classes={{ root: classes.radioRoot }} />}
          label={<Typography className={classes.radioLabel}>Turn on all notifications</Typography>}
        />
        <FormControlLabel
          value="none"
          control={<Radio size="small" classes={{ root: classes.radioRoot }} />}
          label={<Typography className={classes.radioLabel}>Turn off all notifications</Typography>}
        />
        <FormControlLabel
          value="custom"
          control={<Radio size="small" classes={{ root: classes.radioRoot }} />}
          label={<Typography className={classes.radioLabel}>Custom</Typography>}
        />
      </RadioGroup>

      {/* ── events table ─────────────────────────────────────────────────── */}
      <div className={classes.tableWrapper}>
        <Paper className={classes.tablePaper} elevation={0}>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell style={{ minWidth: 320 }}>List of Events</TableCell>
                <TableCell align="center">Dashboard</TableCell>
                <TableCell align="center" style={{ color: '#aaa' }}>Email alert</TableCell>
                <TableCell align="center" style={{ color: '#aaa' }}>WhatsApp</TableCell>
                <TableCell align="center" style={{ color: '#aaa' }}>SMS</TableCell>
                <TableCell align="center" style={{ color: '#aaa' }}>Automated Call</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: 32 }}>
                    <CircularProgress size={28} style={{ color: ORANGE }} />
                  </TableCell>
                </TableRow>
              ) : (
                currentEvents.map((et) => {
                  const s = draft[et.eventTypeID] || { dashboard: false, emailAlert: false };
                  return (
                    <TableRow key={et.eventTypeID} className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>{formatEventName(et.eventTypeName)}</TableCell>
                      <TableCell className={classes.tableCell} align="center">
                        <Checkbox
                          size="small"
                          classes={{ root: classes.checkboxRoot }}
                          checked={s.dashboard}
                          onChange={() => handleCheckboxChange(et.eventTypeID, 'dashboard')}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell} align="center">
                        <Checkbox size="small" classes={{ root: classes.disabledCheckboxRoot }} disabled />
                      </TableCell>
                      <TableCell className={classes.tableCell} align="center">
                        <Checkbox size="small" classes={{ root: classes.disabledCheckboxRoot }} disabled />
                      </TableCell>
                      <TableCell className={classes.tableCell} align="center">
                        <Checkbox size="small" classes={{ root: classes.disabledCheckboxRoot }} disabled />
                      </TableCell>
                      <TableCell className={classes.tableCell} align="center">
                        <Checkbox size="small" classes={{ root: classes.disabledCheckboxRoot }} disabled />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      </div>

      {/* ── unsaved changes dialog ────────────────────────────────────── */}
      <Dialog
        open={unsavedDialogOpen}
        onClose={() => setUnsavedDialogOpen(false)}
        PaperProps={{ className: classes.unsavedDialogPaper }}
      >
        <DialogContent style={{ padding: 0 }}>
          <div className={classes.unsavedCancelRow}>
            <Typography
              className={classes.unsavedCancelText}
              onClick={() => setUnsavedDialogOpen(false)}
            >
              Cancel
            </Typography>
            <IconButton
              size="small"
              style={{ padding: 2, color: '#d32f2f' }}
              onClick={() => setUnsavedDialogOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <Typography className={classes.unsavedTitle}>
            Are you sure you want to exit without saving?
          </Typography>
          <Typography className={classes.unsavedBody}>
            The changes you have made are not saved,<br />
            please confirm if you want to exit without saving.
          </Typography>
          <Button
            className={classes.unsavedConfirmBtn}
            variant="contained"
            disableElevation
            onClick={() => {
              setUnsavedDialogOpen(false);
              history.goBack();
            }}
          >
            Confirm
          </Button>
        </DialogContent>
      </Dialog>

      {/* ── success dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={successOpen}
        onClose={handleSuccessClose}
        PaperProps={{ className: classes.successDialogPaper }}
      >
        <DialogContent style={{ padding: 0 }}>
          <div className={classes.successCloseRow}>
            <IconButton size="small" onClick={handleSuccessClose} style={{ padding: 2 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <CheckCircleOutlineIcon className={classes.successIcon} />
          <Typography className={classes.successTitle}>
            Preferences Saved Successfully!
          </Typography>
          <Typography className={classes.successBody}>
            Your notification preferences have been updated.
          </Typography>
          <Button
            className={classes.successBtn}
            variant="contained"
            disableElevation
            onClick={handleSuccessClose}
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ManageNotifications;
